import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { RefreshCw } from 'lucide-react';
import { ApiError } from '@/lib/api';
import { Modal } from '@/components/ui/modal';
import {
  issueDeleteCaptchaApi,
  type DeleteCaptcha,
  type DeleteCaptchaAction,
} from '@/lib/captcha-api';
import { svgToDataUri } from '@/lib/captcha-render';

/**
 * Issue #235 — 删除验证码 React 钩子 + 展示组件。
 *
 * useDeleteCaptcha：弹窗打开即向后端申领一枚验证码（captchaId + SVG）。删除请求把
 * captchaId + 用户输入回传后端校验；校验失败（一次性已作废）应调用 refresh() 重新申领。
 */
export function useDeleteCaptcha(action: DeleteCaptchaAction) {
  const [captcha, setCaptcha] = useState<DeleteCaptcha | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await issueDeleteCaptchaApi(action);
      setCaptcha(data);
    } catch (e) {
      setCaptcha(null);
      setError(e instanceof ApiError ? e.message : '验证码加载失败，请重试');
    } finally {
      setLoading(false);
    }
  }, [action]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return {
    captchaId: captcha?.captchaId ?? null,
    svg: captcha?.svg ?? null,
    loading,
    error,
    refresh,
  };
}

const INPUT =
  'w-32 rounded-md border border-slate-300 px-2.5 py-1.5 text-sm tracking-widest uppercase focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100';

/**
 * 删除验证码输入区：展示后端渲染的 SVG（可点击刷新）+ 6 位输入框。受控组件，
 * 父弹窗持有 code 与 captchaId（来自 useDeleteCaptcha）。
 */
export function DeleteCaptchaField({
  svg,
  loading,
  error,
  value,
  onChange,
  onRefresh,
}: {
  svg: string | null;
  loading: boolean;
  error: string | null;
  value: string;
  onChange: (v: string) => void;
  onRefresh: () => void;
}) {
  return (
    <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3">
      <div className="mb-1 text-xs font-medium text-slate-600">
        请输入图形验证码（6 位数字 + 字母，防误删）
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onRefresh}
          title="看不清？点击刷新"
          className="flex h-10 w-[120px] items-center justify-center overflow-hidden rounded border border-slate-300 bg-white"
          data-testid="delete-captcha-image"
        >
          {svg ? (
            // Issue #404 加固：改用「图片上下文」渲染，替代 dangerouslySetInnerHTML。
            // 后端 SVG 编码进 data:image/svg+xml URI 后由 <img> 加载——浏览器按图片解析，
            // 即便载荷含 <script>/onload 等也不会执行（img 上下文不跑脚本、不拉外链），
            // 从根上消除仓库内 raw HTML/SVG 注入点；不引入清洗库，保持零依赖。
            <img
              className="h-full w-full object-contain"
              src={svgToDataUri(svg)}
              alt="图形验证码"
              draggable={false}
            />
          ) : (
            <span className="text-xs text-slate-400">{loading ? '加载中…' : '点击获取'}</span>
          )}
        </button>
        <input
          className={INPUT}
          value={value}
          maxLength={6}
          placeholder="验证码"
          autoComplete="off"
          onChange={(e) => onChange(e.target.value)}
          data-testid="delete-captcha-input"
        />
        <button
          type="button"
          onClick={onRefresh}
          className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          刷新
        </button>
      </div>
      {error ? <div className="mt-2 text-xs text-rose-600">{error}</div> : null}
    </div>
  );
}

const PRIMARY_DANGER =
  'inline-flex items-center gap-2 rounded-md bg-rose-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-rose-700 disabled:opacity-60';
const SECONDARY =
  'inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-600 transition hover:bg-slate-50 disabled:opacity-60';

/**
 * 自包含的「删除 + 验证码」确认弹窗。适合原本用 window.confirm 的删除入口直接替换：
 * 父组件只需在删除按钮点击时渲染该弹窗并提供 onConfirm(proof) 执行真正删除。
 * onConfirm 抛 ApiError(400) 时自动刷新验证码并提示重试（验证码一次性）。
 */
export function DeleteWithCaptchaModal({
  action,
  title,
  description,
  body,
  confirmLabel = '确认删除',
  onClose,
  onConfirm,
}: {
  action: DeleteCaptchaAction;
  title: string;
  description?: string;
  body?: ReactNode;
  confirmLabel?: string;
  onClose: () => void;
  onConfirm: (proof: { captchaId: string; captchaCode: string }) => Promise<unknown>;
}) {
  const captcha = useDeleteCaptcha(action);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const submit = async () => {
    if (!captcha.captchaId || code.trim().length !== 6) return;
    setPending(true);
    setError(null);
    try {
      await onConfirm({ captchaId: captcha.captchaId, captchaCode: code });
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '删除失败，请重试。');
      if (e instanceof ApiError && e.status === 400) {
        setCode('');
        void captcha.refresh();
      }
    } finally {
      setPending(false);
    }
  };

  const canDelete = !pending && !!captcha.captchaId && code.trim().length === 6;

  return (
    <Modal
      title={title}
      description={description}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={SECONDARY} onClick={onClose} disabled={pending}>
            取消
          </button>
          <button
            type="button"
            className={PRIMARY_DANGER}
            disabled={!canDelete}
            onClick={() => void submit()}
          >
            {pending ? '删除中…' : confirmLabel}
          </button>
        </>
      }
    >
      {body}
      <DeleteCaptchaField
        svg={captcha.svg}
        loading={captcha.loading}
        error={captcha.error}
        value={code}
        onChange={setCode}
        onRefresh={captcha.refresh}
      />
      {error ? (
        <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-xs text-rose-700">
          {error}
        </div>
      ) : null}
    </Modal>
  );
}
